import moment from "moment";
import { useCallback, useMemo } from "react";
import { MeetingsOverviewMeetingFragment } from "types/graphql-schema";

import MeetingVideoConferenceButton from "@apps/meeting-new/components/meeting/video-conference-button";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import MotionDiv from "@components/motion/motion-div";
import { classNames } from "@helpers/css";
import { getUrl, isTailwindLgActive } from "@helpers/helpers";

const OverviewMeeting = ({
  meeting,
  onClickMeetingLink,
  compact: _compact = false,
  isPast = false,
}: {
  meeting: MeetingsOverviewMeetingFragment;
  hideEditingButtons?: boolean;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
  isPast?: boolean;
}) => {
  const link = useLink();

  const now = Date.now();
  const tsStart = meeting.startDatetime
    ? new Date(meeting.startDatetime).getTime()
    : null;
  const tsEnd = meeting.endDatetime
    ? new Date(meeting.endDatetime).getTime()
    : null;
  const isAboutToStart =
    tsStart && tsEnd ? now > tsStart - 6 * 60 * 1000 && now < tsEnd : false;

  const meetingUrl = useMemo(
    () =>
      getUrl({
        meetingGroupId: meeting.meetingGroupId,
        meetingId: meeting.id,
      }),
    [meeting],
  );

  const handleGoToMeeting = useCallback(() => {
    link.redirect(meetingUrl);
  }, [link, meetingUrl]);

  const handleClickMeetingLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
      if (onClickMeetingLink && meeting.meetingGroup?.id) {
        if (isTailwindLgActive()) {
          e.stopPropagation();
          e.preventDefault();
          onClickMeetingLink(meeting.id, meeting.meetingGroup.id);
        } else {
          handleGoToMeeting();
        }
      }
    },
    [
      meeting.id,
      onClickMeetingLink,
      meeting.meetingGroup?.id,
      handleGoToMeeting,
    ],
  );

  return (
    <>
      <MotionDiv
        className={classNames(
          "text-sm rounded py-1 hover:bg-nav-item-active",
          isPast ? "text-gray-400" : "text-gray-600",
          window.location.pathname === meetingUrl && "bg-nav-item-active",
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        role="button"
        onClick={onClickMeetingLink ? handleClickMeetingLink : undefined}
      >
        <AppLink
          className={classNames(
            "flex items-center gap-2 w-full min-w-0",
            isAboutToStart ? "pl-2 pr-[6px]" : "px-2",
          )}
          to={meetingUrl}
          onClick={handleClickMeetingLink}
        >
          <span className="flex-1 truncate">{meeting.title}</span>
          <span
            className={classNames(
              "text-xs shrink-0",
              isAboutToStart
                ? "bg-blue-400 text-white px-[2px] rounded-sm"
                : isPast
                  ? "text-gray-400"
                  : "text-gray-500",
            )}
          >
            {`${moment(meeting.startDatetime)
              .format("h:mma")
              .replace(":00", "")}`}
          </span>
        </AppLink>
        {meeting.videoConferenceUrl && isAboutToStart && (
          <div className="mt-0.5 pl-1">
            <MeetingVideoConferenceButton
              meeting={meeting}
              className="hover:text-gray-800 hover:bg-black/5 text-xs px-1 h-[22px] rounded text-gray-600 gap-1.5 tracking-tight"
            />
          </div>
        )}
      </MotionDiv>
    </>
  );
};

export default OverviewMeeting;
